import {
    FetchState,
    PartnerPortalState,
    PartnerPortalSingleSignOnConfig,
    PartnerPortalStatus
} from "@/shared/types";
import * as bankService from "@/shared/api/bankService";
import * as storeHelper from "@/shared/storeHelper";
import { defineStore } from "pinia";
import _ from "lodash";

export const initialState: PartnerPortalState = {
    statusFetchState: FetchState.Uninitialized,
    configFetchState: FetchState.Uninitialized,
    status: {
        isAllowed: false,
        hasActiveHsaAccount: false,
        hasActiveNonHsaAccount: false,
    },
    config: {
        action: null,
        value: null
    }
};

export const usePartnerPortalStore = defineStore("partnerPortal", {
    state: (): PartnerPortalState => (_.cloneDeep(initialState)),
    getters: {
        portalConfig: state => state.config,
        /**
         * @deprecated hasPortalAccess was created before the concept of
         * Notional accounts and is only based off of HSA account
         * status, use hasActiveAccount, hasActiveHsaAccount or
         * hasActiveNonHsaAccount for any future logic for access to the system.
         */
        hasPortalAccess: state => state.statusFetchState === FetchState.Loaded && state.status.isAllowed,
        hasActiveAccount: state => state.statusFetchState === FetchState.Loaded &&
            (state.status.hasActiveHsaAccount || state.status.hasActiveNonHsaAccount),
        hasActiveHsa: state => state.statusFetchState === FetchState.Loaded && state.status.hasActiveHsaAccount,
        hasActiveNonHsa: state => state.statusFetchState === FetchState.Loaded && state.status.hasActiveNonHsaAccount,
        portalConfigIsLoaded: state => state.configFetchState === FetchState.Loaded,
        isPortalStatusLoaded: state => state.statusFetchState === FetchState.Loaded
    },
    actions: {
        setStatusFetchState (fetchState: FetchState) {
            this.statusFetchState = fetchState;
        },
        setConfigFetchState (fetchState: FetchState) {
            this.configFetchState = fetchState;
        },
        setPartnerPortalStatus (
            partnerPortalStatus: PartnerPortalStatus) {
            this.statusFetchState = FetchState.Loaded;
            this.status = partnerPortalStatus;
        },
        setPartnerPortalConfig (
            partnerPortalConfig: PartnerPortalSingleSignOnConfig) {
            this.configFetchState = FetchState.Loaded;
            this.config = partnerPortalConfig;
        },
        getPartnerPortalStatus (): Promise<any> {
            if (this.statusFetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }

            this.setStatusFetchState(FetchState.Loading);

            return bankService.partnerPortalStatus()
                .then(response => this.setPartnerPortalStatus(response.data))
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        getPartnerPortalConfig (
            portal: string): Promise<PartnerPortalSingleSignOnConfig> {
            return new Promise((resolve, reject) => {
                this.setConfigFetchState(FetchState.Loading);

                return bankService.partnerPortalSingleSignOnConfig(portal)
                    .then(response => {
                        if (response.data) {
                            this.setPartnerPortalConfig(response.data);
                            return resolve(response.data);
                        }
                        return reject();
                    })
                    .catch(err => storeHelper.handleError({ error: err, rethrow: false }));
            });
        }
    }
});